import type { components, paths } from "@securityjourney/svc-assignments/src/gen/assignments";

import { OpenApiFetchWrapper, type ServiceResponse } from "./openApiFetchWrapper";

export type AssignmentsResponse =
  components["schemas"]["securityjourney.proto.assignments.AssignmentsResponse"];
export type AssignmentsExport =
  components["schemas"]["securityjourney.proto.assignments.AssignmentsExport"];
export type SortBy = components["schemas"]["securityjourney.proto.assignments.SortBy"];
export type OrderType = components["schemas"]["securityjourney.proto.assignments.OrderType"];
export type Filter = components["schemas"]["securityjourney.proto.assignments.Filter"];

class AssignmentsClient extends OpenApiFetchWrapper<paths> {
  constructor() {
    super();
  }

  /**
   * Get a list of assignments for a given tenant.
   *
   * @remarks
   * Returns a paginated list of assignment details, user assignment specifications,
   * and general user progress on assigned content.
   * Accessible only to users with the `tenant-admin` role.
   */
  async assignments(
    tenant: string,
    options?: {
      page?: number;
      limit?: number;
      query?: string;
      sortBy?: SortBy;
      orderType?: OrderType;
      filter?: Filter;
    }
  ) {
    const { page, limit, query, sortBy, orderType, filter } = options ?? {};
    return await this.get("/svc/assignments/{tenant}", {
      params: {
        path: { tenant },
        query: { page, limit, query, sortBy, orderType, filter },
      },
    });
  }

  /**
   * Export assignment data as a CSV download.
   *
   * @remarks
   * Returns exported assignment data for a given tenant.
   * Accessible only to users with the `tenant-admin` role.
   */
  async exportCsv(
    tenant: string,
    page?: number,
    limit?: number,
    query?: string,
    sortBy?: SortBy,
    orderType?: OrderType,
    filter?: Filter
  ): Promise<ServiceResponse<AssignmentsExport>> {
    const result = await this.get("/svc/assignments/{tenant}/export/csv", {
      params: {
        path: { tenant },
        query: { page, limit, query, sortBy, orderType, filter },
      },
    });

    if (result.data) {
      const { data: exportData, contentType, fileName } = result.data;
      if (exportData) {
        const byteCharacters = atob(exportData as string);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: contentType });
        const link = document.createElement("a");
        link.href = window.URL.createObjectURL(blob);
        link.download = fileName as string;
        link.click();
      }
    }

    return result as ServiceResponse<AssignmentsExport>;
  }
}

let assignmentsClientInstance: AssignmentsClient | null = null;

export const getAssignmentsClient = (): AssignmentsClient => {
  if (!assignmentsClientInstance) {
    assignmentsClientInstance = new AssignmentsClient();
  }
  return assignmentsClientInstance;
};
